import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PRICING_HIGHLIGHT } from "../config";
import { useLang } from "../LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

export const Pricing = () => {
  const { t, lang } = useLang();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="pricing" data-testid="pricing" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-16">
          <Reveal><Eyebrow testId="pricing-eyebrow">{t.pricing.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
              {t.pricing.title1} <span className="text-gold-gradient italic">{t.pricing.title2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#a3a3a3] text-base md:text-lg">{t.pricing.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {t.pricing.plans.map((p, i) => {
            const highlight = PRICING_HIGHLIGHT[i];
            return (
              <motion.div
                key={i}
                data-testid={`pricing-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                  highlight
                    ? "bg-gradient-to-b from-[#161206] to-[#0b0b0b] border border-[#d4af37]/50 gold-glow"
                    : "glass"
                }`}
              >
                {highlight && (
                  <span className="absolute top-6 right-6 text-[10px] tracking-[0.25em] uppercase bg-[#d4af37] text-[#050505] px-3 py-1 rounded-full">
                    {lang === "es" ? "Más Popular" : "Most Popular"}
                  </span>
                )}
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37]">{p.name}</div>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display text-5xl md:text-6xl text-white">{p.price}</span>
                  <span className="text-[#a3a3a3] mb-2">/ {t.pricing.oneTime}</span>
                </div>
                <p className="mt-3 text-[#a3a3a3]">{p.tagline}</p>
                <div className="divider-gold my-7" />
                <ul className="space-y-3.5 flex-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#d4d4d4]">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-[#d4af37]" />
                      </span>
                      <span className="text-sm md:text-base">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid={`pricing-get-started-${i}`}
                    onClick={() => go("booking")}
                    className={`flex-1 rounded-full px-6 py-3.5 font-medium transition-colors duration-300 ${
                      highlight ? "bg-[#d4af37] text-[#050505] hover:bg-[#e9d08a]" : "bg-white text-[#050505] hover:bg-[#e9d08a]"
                    }`}
                  >
                    {t.websites.getStarted}
                  </button>
                  <button
                    data-testid={`pricing-contact-${i}`}
                    onClick={() => go("contact")}
                    className="flex-1 rounded-full px-6 py-3.5 glass text-white hover:bg-white/10 transition-colors duration-300"
                  >
                    {t.websites.contactUs}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
